import { apiSlice } from './apiSlice';
import { User, UserRole, LoginCredentials, AuthTokens, ApiResponse } from '@/types';

const mapAdminUser = (response: any): User => ({
  id: response.id.toString(),
  name: `${response.firstname} ${response.lastname}`,
  email: response.email,
  role: UserRole.ADMIN,
  avatar: response.profileImage,
  createdAt: response.createdAt ?? new Date().toISOString(),
  updatedAt: response.updatedAt ?? new Date().toISOString(),
  isActive: response.isActive ?? true,
});

export const authApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<ApiResponse<{ user: User; tokens: AuthTokens }>, LoginCredentials>({
      queryFn: async (credentials, _queryApi, _extraOptions, fetchWithBQ) => {
        try {
          // 1. Authenticate to get the token
          const loginResult = await fetchWithBQ({
            url: '/auth/login',
            method: 'POST',
            body: credentials,
          });

          if (loginResult.error) {
            return { error: loginResult.error as any };
          }

          const tokenData = loginResult.data as {
            accessToken: string;
            expiresIn: number;
            type: string;
          };

          // 2. Fetch the current user profile using the new token
          const userResult = await fetchWithBQ({
            url: '/admin',
            method: 'GET',
            headers: {
              Authorization: `Bearer ${tokenData.accessToken}`,
            },
          });

          if (userResult.error) {
            return { error: userResult.error as any };
          }

          const user = userResult.data as any;

          return {
            data: {
              data: {
                user: mapAdminUser(user),
                tokens: {
                  accessToken: tokenData.accessToken,
                  refreshToken: '', // Backend doesn't use refresh tokens for admin currently
                  expiresIn: tokenData.expiresIn,
                },
              },
            } as any,
          };
        } catch (error: any) {
          return { error: error.response?.data || { message: 'Login failed' } };
        }
      },
      invalidatesTags: ['Auth'],
    }),

    logout: builder.mutation<ApiResponse<null>, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['Auth'],
    }),

    getCurrentUser: builder.query<ApiResponse<User>, string>({
      query: () => '/admin',
      transformResponse: (response: any) => {
        return { data: mapAdminUser(response) } as any;
      },
      providesTags: ['Auth'],
    }),

    changePassword: builder.mutation<
      ApiResponse<null>,
      { oldPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: '/admin/change-password',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useChangePasswordMutation,
} = authApi;
